import { useState, type FC } from 'react'
import { Modal, Stack, Text, Button, Select, Group, Badge } from '@mantine/core'
import {
    marketplaceDestinations,
    MARKETPLACE_DELIVERY_LABEL,
} from '@/lib/marketplaceDestinations'
import { useSegmentStubs } from '../queries'
import type { Destination } from '../types'

const slug = (name: string): string =>
    name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')

// How a destination is reached, shown generically for the picker hint: a direct
// integration, or routed through the data marketplace (provider not surfaced).
const routeHint = (name: string): string => {
    const dest = marketplaceDestinations.find((d) => d.name === name)
    if (!dest) return ''
    if (dest.direct) return 'Direct integration'
    const labels = dest.indirectVia.map((v) => MARKETPLACE_DELIVERY_LABEL[v])
    return `via ${labels.join(' or ')}`
}

export interface AddConnectionModalProps {
    // Platforms already in the list (excluded from the picker).
    existingPlatforms: string[]
    opened: boolean
    onClose: () => void
    // Called with the newly-added destination so the caller can open its
    // connection setup immediately.
    onAdded: (destination: Destination) => void
}

// Add a destination from the marketplace catalog to the connectable list. The
// destination is added in a needs-setup state; the caller then opens its
// connection editor to enter credentials.
export const AddConnectionModal: FC<AddConnectionModalProps> = ({
    existingPlatforms,
    opened,
    onClose,
    onAdded,
}) => {
    const { addDestination } = useSegmentStubs()
    const [platform, setPlatform] = useState<string | null>(null)

    const options = marketplaceDestinations
        .filter((d) => !existingPlatforms.includes(d.name))
        .map((d) => ({ value: d.name, label: d.name }))

    const handleAdd = () => {
        if (!platform) return
        const destination: Destination = {
            id: slug(platform),
            platform,
        }
        addDestination(destination)
        onAdded(destination)
        setPlatform(null)
        onClose()
    }

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            centered
            size='md'
            title={<Text fw={600}>Add a connection</Text>}
        >
            <Stack gap='md'>
                <Text size='sm' c='dimmed'>
                    Pick a destination to deliver to. You&apos;ll enter its
                    connection details next.
                </Text>
                <Select
                    label='Destination'
                    placeholder='Search destinations'
                    searchable
                    data={options}
                    value={platform}
                    onChange={setPlatform}
                    nothingFoundMessage='No matching destinations'
                />
                {platform && (
                    <Group gap='xs'>
                        <Text size='sm' c='dimmed'>
                            Reached:
                        </Text>
                        <Badge variant='light'>{routeHint(platform)}</Badge>
                    </Group>
                )}
                <Group justify='flex-end' mt='xs'>
                    <Button variant='default' onClick={onClose}>
                        Cancel
                    </Button>
                    <Button disabled={!platform} onClick={handleAdd}>
                        Add &amp; set up
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
}
